import React, { useState } from 'react';
import { Check, FileDown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { downloadChallan, errMsg, fmtQ } from './srUtils';

const tid = (r) => r.return_no.replace(/[^A-Za-z0-9]/g, '-');

export const ChallanButton = ({ ret }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); await downloadChallan(ret); setBusy(false); };
  return (
    <Button variant="outline" onClick={go} disabled={busy} className="border-[#2D5F4F] text-[#2D5F4F]" data-testid={`sr-challan-btn-${tid(ret)}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}Download Challan
    </Button>
  );
};

export const ApproveButton = ({ ret, onDone }) => {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/supplier-returns/${ret.return_id}/approve`);
      toast.success(`${ret.return_no} approved — the stock has gone back to ${ret.vendor_name}. Download the challan from History.`);
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not approve'), { duration: 9000 }); } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button className="bg-[#2D5F4F] hover:bg-[#234A3D]" disabled={busy} data-testid={`sr-approve-btn-${tid(ret)}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Approve
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="sr-approve-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Approve {ret.return_no}?</AlertDialogTitle>
          <AlertDialogDescription>
            {fmtQ(ret.total_qty)} pcs leave the stock for good and go back to {ret.vendor_name}.
            {ret.above_po_qty > 0 ? ` The PO received qty drops by ${fmtQ(ret.above_po_qty)} pcs.` : ''} A Return Challan is made for the supplier.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="sr-approve-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={approve} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="sr-approve-confirm">Approve return</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const RejectButton = ({ ret, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reject = async () => {
    if (reason.trim().length < 3) return toast.error('Type why this return is rejected');
    setBusy(true);
    try {
      await apiClient.post(`/supplier-returns/${ret.return_id}/reject`, { reason: reason.trim() });
      toast.success(`${ret.return_no} rejected — the stock is back for use`);
      setOpen(false); onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not reject'), { duration: 9000 }); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`sr-reject-btn-${tid(ret)}`}><X className="w-4 h-4 mr-2" />Reject</Button>
      </DialogTrigger>
      <DialogContent data-testid="sr-reject-dialog">
        <DialogHeader><DialogTitle>Reject {ret.return_no}?</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">The held stock goes back into use. Tell the staff why.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this return rejected?" rows={3} data-testid="sr-reject-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="sr-reject-cancel">Cancel</Button>
          <Button onClick={reject} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="sr-reject-confirm">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Reject return
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
